import cn from 'classnames'
import { useOutsideClick } from 'mmfintech-commons'
import type { ReactNode, RefObject } from 'react'
import { useEffect, useId, useRef, useState } from 'react'
import styles from './FloatingSupportMenu.module.scss'
import type { FloatingSupportMenuItem } from './FloatingSupportMenu.types'

interface LoggedOutSupportMenuProps {
  items: FloatingSupportMenuItem[]
  triggerIcon: ReactNode
  triggerLabel?: string
  onOpenChange?: (open: boolean) => void
}

const CloseIcon = () => (
  <svg
    viewBox='0 0 24 24'
    width='20'
    height='20'
    fill='none'
    stroke='currentColor'
    strokeWidth={2}
    strokeLinecap='round'
  >
    <line x1='6' y1='6' x2='18' y2='18' />
    <line x1='18' y1='6' x2='6' y2='18' />
  </svg>
)

/**
 * Dropdown fab for the logged-out state: click toggles a menu built from `items`
 * (empty array renders nothing). Escape, outside click, and Tab-out of the menu all
 * close it and return focus to the trigger.
 */
function LoggedOutSupportMenu(props: LoggedOutSupportMenuProps) {
  const { items, triggerIcon, triggerLabel, onOpenChange } = props

  const [open, setOpenState] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()

  function setOpen(next: boolean) {
    setOpenState(next)
    onOpenChange?.(next)
  }

  // useOutsideClick's declared return type (RefObject<T | null>) doesn't structurally match
  // the JSX `ref` attribute's expected RefObject<T> under this project's @types/react version
  // even though both allow a null `current` at runtime — narrow the type here.
  const containerRef = useOutsideClick<HTMLDivElement>(() => setOpen(false)) as RefObject<HTMLDivElement>

  // setOpen is a plain function redefined every render (not memoized) — adding it to the deps
  // below would re-subscribe the listeners on every render instead of only when open/containerRef
  // actually change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    if (!open) return

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    function handleFocusOut(event: FocusEvent) {
      const next = event.relatedTarget as Node | null
      if (containerRef.current && (!next || !containerRef.current.contains(next))) {
        setOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('focusout', handleFocusOut)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('focusout', handleFocusOut)
    }
  }, [open, containerRef])

  if (!items.length) return null

  return (
    <div ref={containerRef} className={cn(styles['floating-support-menu'])}>
      <button
        ref={triggerRef}
        type='button'
        className={cn(styles['floating-support-menu-trigger'])}
        aria-haspopup='menu'
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={triggerLabel}
        onClick={() => setOpen(!open)}
      >
        {open ? <CloseIcon /> : triggerIcon}
      </button>
      {/* Trigger renders before the dropdown in the DOM (visual position comes entirely from
          `position: absolute` in the .module.scss, independent of DOM order) so that Tab,
          pressed right after opening — while focus is still on the trigger — moves forward
          INTO the menu items instead of skipping past them to whatever's next on the page. */}
      <div
        id={menuId}
        role='menu'
        aria-hidden={!open}
        // Always mounted (not `{open && <div/>}`) — a pure CSS transition on opacity/transform
        // needs the element present for both directions; unmounting on close gives it no time
        // to animate out. `visibility` (delayed on close, instant on open, see .module.scss)
        // keeps it non-interactive/untabbable while closed without any JS timing logic.
        className={cn(styles['floating-support-menu-dropdown'], {
          [styles['floating-support-menu-dropdown-open']]: open
        })}
      >
        {items.map((item, index) =>
          item.href ? (
            <a
              // `items` is a static, portal-supplied list — same order every render, never
              // reordered/filtered/inserted into dynamically.
              // biome-ignore lint/suspicious/noArrayIndexKey: see comment above
              key={index}
              role='menuitem'
              href={item.href}
              target={item.target}
              rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
              className={cn(styles['floating-support-menu-item'])}
              onClick={() => setOpen(false)}
            >
              <span className={cn(styles['floating-support-menu-item-icon'])}>{item.icon}</span>
              <span className={cn(styles['floating-support-menu-item-label'])}>{item.label}</span>
            </a>
          ) : (
            <button
              // `items` is a static, portal-supplied list — same order every render, never
              // reordered/filtered/inserted into dynamically.
              // biome-ignore lint/suspicious/noArrayIndexKey: see comment above
              key={index}
              type='button'
              role='menuitem'
              className={cn(styles['floating-support-menu-item'])}
              onClick={() => {
                if (!item.keepOpenOnClick) setOpen(false)
                item.onClick?.()
              }}
            >
              <span className={cn(styles['floating-support-menu-item-icon'])}>{item.icon}</span>
              <span className={cn(styles['floating-support-menu-item-label'])}>{item.label}</span>
            </button>
          )
        )}
      </div>
    </div>
  )
}

export default LoggedOutSupportMenu
